import { useEffect, useState } from 'react';
import {
    enqueueLowUrl,
    focusPictures,
    pictureUrls,
    slideFiles,
    subscribeNetworkTier,
    viewerTargetWidth,
    warmPicture,
} from '../picture';

function slideKey(slide) {
    if (!slide) return '';
    if (slide.id) return String(slide.id);
    if (Array.isArray(slide.images)) {
        return slide.images.map((image) => image.id).filter(Boolean).join('+');
    }
    if (typeof slide.url === 'string') return slide.url;
    return slide.url?.original || '';
}

const AHEAD_SLIDES = 2;

/** Slides around the open one whose grid files keep loading while slides change fast, nearest first. */
const NEAR_OFFSETS = [1, 2, -1, 3, 4, -2];

function slidesAhead(gallery, currentId) {
    if (!currentId) return [];
    const index = gallery.findIndex((slide) => slideKey(slide) === currentId);
    if (index < 0) return [];
    const ahead = [];
    for (let delta = 1; delta <= AHEAD_SLIDES; delta += 1) {
        if (gallery[index + delta]) ahead.push(gallery[index + delta]);
    }
    return ahead;
}

function slidesNear(gallery, index) {
    if (index < 0) return [];
    return NEAR_OFFSETS.map((delta) => gallery[index + delta]).filter(Boolean);
}

/** The gallery from the open slide outwards, so the grid queue fills in around where the viewer is. */
function byDistance(gallery, index) {
    if (index < 0) return gallery;
    return gallery
        .map((slide, at) => ({ slide, distance: at >= index ? at - index : (index - at) + 0.5 }))
        .sort((a, b) => a.distance - b.distance)
        .map(({ slide }) => slide);
}

function sharpUrl(file, target) {
    const urls = pictureUrls(file, target);
    return urls.sharp || urls.low || '';
}

/**
 * The open slide's grid files go first, then its full-size files, both pages
 * of a spread together, and pause the low-res queue. Grid files of the slides
 * around it follow, and keep loading while slides change fast, so skimming
 * shows a low-res picture at once and sharpens where it stops. The next two
 * slides then load at full size, one slide after another (not on a slow link).
 * Every other picture is only queued at the grid rung, nearest first.
 * Masonry uses the same order: the open picture, then two slides ahead.
 */
export const useImagePreload = (prev, next, gallery, current) => {
    const prevId = slideKey(prev);
    const nextId = slideKey(next);
    const currentId = slideKey(current);
    const galleryKey = Array.isArray(gallery) ? gallery.map(slideKey).join('|') : '';
    const [tier, setTier] = useState(null);

    useEffect(() => subscribeNetworkTier(setTier), []);

    useEffect(() => {
        const target = viewerTargetWidth();
        const slides = Array.isArray(gallery) ? gallery : null;
        const filesOf = (list) => list.flatMap((slide) => slideFiles(slide));
        if (slides) {
            const index = currentId ? slides.findIndex((slide) => slideKey(slide) === currentId) : -1;
            const currentSlide = index >= 0 ? slides[index] : null;
            const currentFiles = filesOf(currentSlide ? [currentSlide] : []);
            const ahead = slidesAhead(slides, currentId).map((slide) => (
                slideFiles(slide).map((file) => sharpUrl(file, target)).filter(Boolean)
            )).filter((group) => group.length);
            focusPictures(
                currentFiles.map((file) => sharpUrl(file, target)),
                ahead,
                currentFiles.map((file) => pictureUrls(file, target).low),
                filesOf(slidesNear(slides, index)).map((file) => pictureUrls(file, target).low),
            );
            byDistance(slides, index).forEach((slide) => {
                slideFiles(slide).forEach((file) => {
                    const { low } = pictureUrls(file, target);
                    if (low) enqueueLowUrl(low);
                });
            });
            return;
        }
        filesOf([prev, next]).forEach((file) => warmPicture(file, target, 'low'));
    }, [prevId, nextId, currentId, galleryKey, tier]);
};

export default useImagePreload;
